import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Box,
  Text,
  SimpleGrid,
  Spinner,
  Alert,
  Table,
  Icon,
  Portal,
  CloseButton,
} from '@chakra-ui/react';
import { FiUsers, FiHome, FiMapPin, FiCheck, FiClock } from 'react-icons/fi'
import { TurfMap, TurfLegendPanel, OverlayModeToggle } from '../turf'
import { ContactDetailModal } from '../contactDetail'
import { BusinessDetailModal } from '../business/BusinessDetailModal'
import { useTurfMapDetail, useTurfMapHouseholds, useTurfMapContactsWithEmployment } from '../../hooks/useTurfMap'
import { groupContactsByWorkplace, splitByEmploymentStatus, contactsToHouseholdMarkers, applyTurfVisitedStatus } from '../turf/workplaceUtils'
import { TURF_COLORS } from '../../lib/mapConfig'
import { addressModeToOverlayMode } from '../../utils/geoUtils'
import type { Map as MapLibreMap } from 'maplibre-gl'
import type * as GeoJSON from 'geojson'
import type { OverlayMode } from '../../types/workplaceOverlay'
import type { HouseholdMarker, TurfBoundary } from '../../types/turfMap'
import type { TurfLegendMethods } from '../turf/TurfLegendPanel'
import { formatEstimatedMinutes, sumEstimatedMinutes } from '../turfMapOverview/turfEstimate'

interface TurfMapViewerDrawerProps {
  isOpen: boolean
  onClose: () => void
  turfMapId: string | null
}

export function TurfMapViewerDrawer({ isOpen, onClose, turfMapId }: TurfMapViewerDrawerProps) {
  const { data: turfMap, isLoading, error } = useTurfMapDetail(
    isOpen && turfMapId ? turfMapId : undefined
  )
  const { data: households } = useTurfMapHouseholds(
    isOpen && turfMapId ? turfMapId : undefined
  )

  const [overlayMode, setOverlayMode] = useState<OverlayMode>('home')

  // Contact/Worksite detail modal state
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [selectedWorksiteId, setSelectedWorksiteId] = useState<string | null>(null)
  const [isWorksiteModalOpen, setIsWorksiteModalOpen] = useState(false)

  const handleContactClick = (contactId: string) => {
    setSelectedContactId(contactId)
    setIsContactModalOpen(true)
  }

  const handleWorksiteClick = (businessId: string) => {
    setSelectedWorksiteId(businessId)
    setIsWorksiteModalOpen(true)
  }

  // Map ref for fly-to-turf functionality
  const mapRef = useRef<MapLibreMap | null>(null)

  const handleMapLoad = useCallback((map: MapLibreMap) => {
    mapRef.current = map
  }, [])

  // Default overlay mode to the TurfMap's saved addressMode
  useEffect(() => {
    if (turfMap) {
      setOverlayMode(addressModeToOverlayMode(turfMap.addressMode))
    }
  }, [turfMap?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employment data when overlay mode is work or hybrid
  const { data: contactsWithEmployment } = useTurfMapContactsWithEmployment(
    turfMap?.id,
    isOpen && overlayMode !== 'home'
  )

  const turfBoundaries: TurfBoundary[] = useMemo(() => {
    const results: TurfBoundary[] = []
    for (const [idx, t] of (turfMap?.turfs ?? []).entries()) {
      if (!t.boundaryGeoJson) continue
      try {
        const geometry = JSON.parse(t.boundaryGeoJson)
        results.push({
          id: t.id,
          label: t.label,
          color: TURF_COLORS[idx % TURF_COLORS.length],
          geoJson: { type: 'Feature' as const, geometry, properties: {} },
        })
      } catch {
        // skip invalid
      }
    }
    return results
  }, [turfMap?.turfs])

  const flyToTurf = useCallback(
    (turfId: string) => {
      const boundary = turfBoundaries.find(b => b.id === turfId)
      if (!boundary || !mapRef.current) return

      const geometry = boundary.geoJson.geometry
      let outerRing: GeoJSON.Position[]
      if (geometry.type === 'Polygon') {
        outerRing = geometry.coordinates[0]
      } else {
        outerRing = geometry.coordinates[0][0]
      }

      const bounds = outerRing.reduce(
        (b, coord) => [
          [Math.min(b[0][0], coord[0] as number), Math.min(b[0][1], coord[1] as number)],
          [Math.max(b[1][0], coord[0] as number), Math.max(b[1][1], coord[1] as number)],
        ],
        [
          [outerRing[0][0] as number, outerRing[0][1] as number],
          [outerRing[0][0] as number, outerRing[0][1] as number],
        ]
      )

      mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
        padding: 50,
        duration: 800,
      })
    },
    [turfBoundaries]
  )

  const legendMethods: TurfLegendMethods = useMemo(() => ({
    deleteTurf: () => {},
    updateTurfLabel: () => {},
    flyToTurf,
  }), [flyToTurf])

  const homeHouseholdMarkers: HouseholdMarker[] = useMemo(
    () =>
      households?.map((h) => ({
        householdKey: h.householdKey,
        longitude: h.longitude,
        latitude: h.latitude,
        streetAddress: h.streetAddress,
        city: h.city,
        state: h.state,
        zip: h.zip,
        contactCount: h.contacts.length,
        visitedCount: h.contacts.filter(c => c.isVisited).length,
        needsRevisitCount: h.contacts.filter(c => c.isVisited && (c.contactStatus === 4 || c.contactStatus === 5)).length,
        contacts: h.contacts.map((c) => ({
          contactId: c.contactId,
          contactName: c.contactName,
          walkOrder: c.walkOrder,
          isVisited: c.isVisited,
          contactStatus: c.contactStatus,
          turfId: c.turfId,
          turfIndex: c.turfIndex,
        })),
      })) ?? [],
    [households]
  )

  // Overlay turf-specific isVisited from household data onto employment contacts
  const enrichedEmployment = useMemo(
    () => contactsWithEmployment ? applyTurfVisitedStatus(contactsWithEmployment, households) : undefined,
    [contactsWithEmployment, households]
  )

  // Decide workplace markers and hybrid households based on overlay mode
  const { workplaceMarkers, hybridHouseholds } = useMemo(() => {
    if (!enrichedEmployment || overlayMode === 'home') {
      return { workplaceMarkers: undefined, hybridHouseholds: undefined }
    }

    if (overlayMode === 'work') {
      return {
        workplaceMarkers: groupContactsByWorkplace(enrichedEmployment),
        hybridHouseholds: undefined,
      }
    }

    // Hybrid mode: workplaces for employed, households for unemployed
    const { withWork, withoutWork } = splitByEmploymentStatus(enrichedEmployment)
    return {
      workplaceMarkers: groupContactsByWorkplace(withWork),
      hybridHouseholds: contactsToHouseholdMarkers(withoutWork),
    }
  }, [enrichedEmployment, overlayMode])

  // Select which markers to show based on overlay mode
  const effectiveHouseholds = overlayMode === 'work'
    ? []
    : overlayMode === 'hybrid' && hybridHouseholds
      ? hybridHouseholds
      : homeHouseholdMarkers

  const totalContacts = turfMap?.totalContactCount ?? 0
  const totalVisited = turfMap?.totalVisitedCount ?? 0
  const totalHouseholds = turfMap?.totalHouseholdCount ?? 0
  const turfs = turfMap?.turfs ?? []

  const progressPercent =
    totalContacts > 0
      ? Math.round((totalVisited / totalContacts) * 100)
      : 0

  return (
    <Drawer.Root open={isOpen} size='full' placement='end' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger zIndex={10} asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px">
              {turfMap ? (
                <HStack gap={3}>
                  <Text>{turfMap.label}</Text>
                  <OverlayModeToggle mode={overlayMode} onChange={setOverlayMode} />
                </HStack>
              ) : (
                <Text>Turf Map</Text>
              )}
            </Drawer.Header>
            <Drawer.Body p={6} overflowY="auto">
              {isLoading && (
                <VStack py={12}>
                  <Spinner size="lg" color="accent" />
                  <Text color="gray.500">Loading turf map...</Text>
                </VStack>
              )}

              {!!error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  Failed to load turf map.
                </Alert.Root>
              )}

              {turfMap && (
                <VStack gap={6} align="stretch">
                  {/* Stats */}
                  <Box bg="surface" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
                    <SimpleGrid columns={{ base: 2, md: 5 }} gap={4}>
                      <Box>
                        <HStack gap={2} color="gray.500" mb={1}>
                          <Icon boxSize={3} asChild><FiUsers /></Icon>
                          <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Contacts</Text>
                        </HStack>
                        <Text fontWeight="bold">{totalContacts.toLocaleString()}</Text>
                      </Box>
                      <Box>
                        <HStack gap={2} color="gray.500" mb={1}>
                          <Icon boxSize={3} asChild><FiHome /></Icon>
                          <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Households</Text>
                        </HStack>
                        <Text fontWeight="bold">{totalHouseholds.toLocaleString()}</Text>
                      </Box>
                      <Box>
                        <HStack gap={2} color="gray.500" mb={1}>
                          <Icon boxSize={3} asChild><FiMapPin /></Icon>
                          <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Turfs</Text>
                        </HStack>
                        <Text fontWeight="bold">{turfs.length}</Text>
                      </Box>
                      <Box>
                        <HStack gap={2} color="gray.500" mb={1}>
                          <Icon boxSize={3} asChild><FiCheck /></Icon>
                          <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Completion</Text>
                        </HStack>
                        <Text fontWeight="bold">
                          {progressPercent}%
                          <Text as="span" fontSize="sm" color="gray.400" ml={1}>
                            ({totalVisited}/{totalContacts})
                          </Text>
                        </Text>
                      </Box>
                      <Box>
                        <HStack gap={2} color="gray.500" mb={1}>
                          <Icon boxSize={3} asChild><FiClock /></Icon>
                          <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Est. Time</Text>
                        </HStack>
                        <Text fontWeight="bold">
                          {formatEstimatedMinutes(sumEstimatedMinutes(turfs))}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  {/* Map */}
                  <Box position="relative" borderRadius="lg" overflow="hidden" border="1px" borderColor="gray.200">
                    <TurfMap
                      boundaries={turfBoundaries}
                      households={effectiveHouseholds}
                      workplaces={workplaceMarkers}
                      onHouseholdContactClick={handleContactClick}
                      onWorkplaceContactClick={handleContactClick}
                      onWorksiteClick={handleWorksiteClick}
                      onBoundaryClick={flyToTurf}
                      onMapLoad={handleMapLoad}
                      height="450px"
                    />
                    <TurfLegendPanel
                      turfs={turfs.map((t, idx) => ({
                        id: t.id,
                        index: idx + 1,
                        label: t.label,
                        color: TURF_COLORS[idx % TURF_COLORS.length],
                        contactCount: t.memberCount,
                        householdCount: households?.filter((h) =>
                          h.contacts.some((c) => c.turfId === t.id)
                        ).length ?? 0,
                      }))}
                      readonly
                      methods={legendMethods}
                    />
                  </Box>

                  {/* Turfs Table */}
                  {turfs.length > 0 && (
                    <Box border="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                      <Table.Root size="sm">
                        <Table.Header bg="gray.50">
                          <Table.Row>
                            <Table.ColumnHeader w="50px">#</Table.ColumnHeader>
                            <Table.ColumnHeader>Label</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Contacts</Table.ColumnHeader>
                            <Table.ColumnHeader>Assigned Today</Table.ColumnHeader>
                            <Table.ColumnHeader>Progress</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {turfs.map((turf, idx) => {
                            const pct = turf.memberCount > 0
                              ? Math.round((turf.visitedCount / turf.memberCount) * 100)
                              : 0
                            return (
                              <Table.Row key={turf.id}>
                                <Table.Cell>
                                  <HStack gap={2}>
                                    <Box w="10px" h="10px" borderRadius="sm" bg={TURF_COLORS[idx % TURF_COLORS.length]} />
                                    <Text fontSize="sm" color="gray.500">{idx + 1}</Text>
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell><Text fontSize="sm" fontWeight="medium">{turf.label}</Text></Table.Cell>
                                <Table.Cell textAlign='end'><Text fontSize="sm">{turf.memberCount}</Text></Table.Cell>
                                <Table.Cell><Text fontSize="sm">{turf.assignedToName ?? '-'}</Text></Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm">
                                    {turf.visitedCount}/{turf.memberCount}
                                    <Text as="span" color="gray.400" ml={1}>({pct}%)</Text>
                                  </Text>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  )}
                </VStack>
              )}
            </Drawer.Body>

            {/* Contact Detail Modal */}
            {selectedContactId && (
              <ContactDetailModal
                contactId={selectedContactId}
                isOpen={isContactModalOpen}
                onClose={() => {
                  setIsContactModalOpen(false)
                  setSelectedContactId(null)
                }}
              />
            )}

            {/* Business Detail Modal */}
            {selectedWorksiteId && (
              <BusinessDetailModal
                worksiteId={selectedWorksiteId}
                isOpen={isWorksiteModalOpen}
                onClose={() => {
                  setIsWorksiteModalOpen(false)
                  setSelectedWorksiteId(null)
                }}
                onEmployeeClick={handleContactClick}
              />
            )}
          </Drawer.Content>
        </Drawer.Positioner>

      </Portal>
    </Drawer.Root>
  );
}
